import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, ExternalLink, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { cafeData } from '../data/cafeData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Thank you for your message! We\'ll get back to you soon.' 
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Sorry, something went wrong. Please try again or visit us in person.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Get in Touch</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-stone-500 text-lg md:text-xl leading-relaxed">
              Have a question or feedback? We'd love to hear from you. Drop by the café or send us a message.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-32 px-6 md:px-12" data-testid="contact-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl font-bold text-[#1C1917] mb-8">
                Send Us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-stone-700 mb-2 block">Your Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="w-full bg-white border-stone-200 focus:border-[#3A4D39] focus:ring-1 focus:ring-[#3A4D39] rounded-sm h-12"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700 mb-2 block">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="w-full bg-white border-stone-200 focus:border-[#3A4D39] focus:ring-1 focus:ring-[#3A4D39] rounded-sm h-12"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-stone-700 mb-2 block">Your Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    required
                    rows={6}
                    className="w-full bg-white border-stone-200 focus:border-[#3A4D39] focus:ring-1 focus:ring-[#3A4D39] rounded-sm resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {status.message && (
                  <div 
                    className={`flex items-center gap-3 p-4 rounded-sm ${
                      status.type === 'success' 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                    data-testid="contact-status-message"
                  >
                    {status.type === 'success' 
                      ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      : <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    }
                    <p>{status.message}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#3A4D39] hover:bg-[#2C3B2B] text-white py-4 text-base font-medium disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </motion.div>

            {/* Contact Info & Map */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-3xl font-bold text-[#1C1917] mb-8">
                Visit Our Café
              </h2>

              {/* Map */}
              <div className="w-full h-64 md:h-80 border border-stone-200 overflow-hidden mb-8" data-testid="contact-map">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2489.8!2d${cafeData.coordinates.lng}!3d${cafeData.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDI1JzEzLjEiTiAwwrAxMScxNC4wIlc!5e0!3m2!1sen!2suk!4v1635000000000!5m2!1sen!2suk`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Cafe 110 Location"
                />
              </div>

              {/* Address */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#3A4D39]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#3A4D39]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1C1917] mb-1">Address</h3>
                    <p className="text-stone-500">{cafeData.address.street}</p>
                    <p className="text-stone-500">{cafeData.address.area}</p>
                    <p className="text-stone-500">{cafeData.address.postcode}</p>
                    <p className="text-stone-400 text-sm mt-2">{cafeData.distanceFromStation} from {cafeData.nearestStation} Station</p>
                    <a
                      href={cafeData.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#3A4D39] hover:text-[#2C3B2B] font-medium text-sm mt-3"
                      data-testid="contact-directions-link"
                    >
                      Get Directions
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#3A4D39]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#3A4D39]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1C1917] mb-2">Opening Hours</h3>
                    <div className="space-y-1">
                      {cafeData.hours.map((item) => (
                        <div key={item.day} className="flex justify-between text-sm text-stone-500 w-48">
                          <span>{item.day}</span>
                          <span>{item.open} - {item.close}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": cafeData.name,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": cafeData.address.street,
              "addressLocality": cafeData.address.area,
              "postalCode": cafeData.address.postcode,
              "addressCountry": "GB"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": cafeData.coordinates.lat,
              "longitude": cafeData.coordinates.lng
            }
          }
        })
      }} />
    </div>
  );
};
